"use client"
import { useEffect, useState, useCallback } from "react"
import Cookies from "js-cookie"
import { useRouter } from "next/navigation"
import { ContentfulLivePreview } from "@contentful/live-preview"
import { getProfile } from "@/profiles/getProfile"

function extractPrefix(input:string) {
  const match2 = input.match(/^\/?([a-zA-Z]{2}(?:-[a-zA-Z]{2,4})?)(?:\/.*)?$/);
  return match2 ? match2[1] : null;
}
function removeLocaleFromString(pathname:any): any {
  const possibleLocale = extractPrefix(pathname)
  if(possibleLocale !== null) { pathname = pathname.replace(`/${possibleLocale}`, '') }
  return pathname;
}
function stripBasePath(pathname: string, baseUrl: string): string {
  try {
    const basePath = new URL(baseUrl).pathname
    if (basePath && basePath !== '/') {
      return pathname.replace(basePath, "")
    }
  } catch {}
  return pathname
}

const useLanguage = (
  location: any,
  languages: any,
  profile: any,
) => {
  const [languageSettings, setLanguageSettings] = useState({ language: null })
  const router = useRouter();
  
  // Check if user is authenticated
  const isUserAuthenticated = useCallback(() => {
    if (typeof window !== "undefined") {
      const userInfo = localStorage?.getItem("user_info");
      const userTrialData = localStorage?.getItem("user-trial-data");
      
      if (userInfo || userTrialData) {
        try {
          const parsedUserInfo = userInfo ? JSON.parse(userInfo) : null;
          const parsedTrialData = userTrialData ? JSON.parse(userTrialData) : null;
          
          const hasValidUserInfo = parsedUserInfo?.tid;
          const hasValidTrialData = parsedTrialData?.id;
          
          if (hasValidUserInfo || hasValidTrialData) {
            return true;
          }
        } catch (e) {
          return false;
        }
      }
    }
    return false;
  }, []);
  
  const saveLanguageSettings = useCallback((newLanguage: any) => {
    // handles setting cookie/localstorage for language, and redirecting to the new language URL
    if (newLanguage) {
      newLanguage.language["code"] = newLanguage.language.code
      const updatedLang = JSON.stringify({ 
        language: {
          nativeField: newLanguage?.language?.nativeField,
          translatableName: newLanguage?.language?.translatableName,
          code: newLanguage?.language?.code,
        }
      })
      
      const isAuthenticated = isUserAuthenticated();
      
      if (
        location?.host?.includes("create.netlify.com") ||
        location?.host?.includes("localhost:3000")
      ) {
        localStorage.setItem("rcLanguageSettings", updatedLang)
      } else {
        Cookies.set("rcLanguageSettings", updatedLang)
      }

      let path = ""
      // if (location?.pathname) {
      //   const slashIndex = location?.pathname?.indexOf("/", 1)
      //   path = slashIndex !== -1 ? location?.pathname?.slice(slashIndex) : ""
      // }
      if (location?.pathname) {
        path = stripBasePath(location?.pathname, profile.BASE_URL)
      }
      const redirectLangBypass = Cookies.get("redirectLangBypass") // used by Algolia to see pages normally redirected
      
      // Redirect to the new language page when user manually switches language
      // This applies to both authenticated and anonymous users when using the language switcher
      if(ContentfulLivePreview?.initialized === false && redirectLangBypass !== "true"){
        const pathRemoveLang = removeLocaleFromString(path)
        const newPath = `${profile.BASE_URL}/${newLanguage?.language?.code}${pathRemoveLang}${location?.search}`
        router?.push(newPath)
      }
    }
  }, [location, router, profile.BASE_URL, isUserAuthenticated]);
  useEffect(() => {
    const checkValidLanguage = (langCode: string) => {
      let returnedLang
      languages?.forEach((language: any) => {
        if (language?.code === langCode) {
          const newLanguage = {
            language: language
          }
          returnedLang = newLanguage
        }
      })
      return returnedLang;
    }
    const detectLanguage = () => {
      let detectedLanguage: any;
      const isAuthenticated = isUserAuthenticated();
      const pathWithoutBase = stripBasePath(location?.pathname, profile.BASE_URL);
      
      // For authenticated users, use stored preferences (cookie/localStorage)
      // For anonymous users, use URL-based language detection
      
      if (isAuthenticated) {
        // Authenticated user: check cookie/localStorage first
        if (localStorage?.getItem("rcLanguageSettings")) {
          // IF LOCALSTORAGE IS SET, USE LOCALSTORAGE VALUE FOR LANG
          const localStorageLang = localStorage?.getItem("rcLanguageSettings");
          const parsedLang = localStorageLang && JSON?.parse(localStorageLang)
          detectedLanguage = checkValidLanguage(parsedLang?.language?.code);
        } else if (Cookies.get("rcLanguageSettings")){
          // IF COOKIE IS SET, USE COOKIE VALUE FOR LANG
          const cookieLang = Cookies.get("rcLanguageSettings");
          const parsedLang = cookieLang && JSON?.parse(cookieLang)
          detectedLanguage = checkValidLanguage(parsedLang?.language?.code);
        } else {
          // Fallback to URL detection if no preferences stored
          const langFromUrl = extractPrefix(pathWithoutBase);
          detectedLanguage = langFromUrl ? checkValidLanguage(langFromUrl) : {
            language: {
              nativeField: "English (United States)",
              translatableName: "English",
              code: "en-US",
            }
          }
        }
      } else {
        // Anonymous user: always detect language from URL
        const langFromUrl = extractPrefix(pathWithoutBase);
        if (langFromUrl && checkValidLanguage(langFromUrl)) {
          detectedLanguage = checkValidLanguage(langFromUrl);
        } else {
          // Default to English if no valid language in URL
          detectedLanguage = {
            language: {
              nativeField: "English (United States)",
              translatableName: "English",
              code: "en-US",
            }
          }
        }
      }
      
      setLanguageSettings(detectedLanguage)
    } 
    detectLanguage()
  }, [languages, location?.href, location?.pathname, location?.host, isUserAuthenticated])
  return { languageSettings, saveLanguageSettings }
}

export default useLanguage
